export const aiShorts = () => {
  const root = document.querySelector('[data-ai-shorts]');
  if (!root) return;

  const filter = root.querySelector('[data-shorts-filter]');
  const cards = [...root.querySelectorAll('[data-shorts-card]')];
  const previewTriggers = [...root.querySelectorAll('[data-shorts-preview-trigger]')];
  const count = root.querySelector('[data-shorts-count]');
  const empty = root.querySelector('[data-shorts-empty]');
  const grid = root.querySelector('[data-shorts-grid]');
  const pagination = root.querySelector('[data-shorts-pagination]');
  const pageList = root.querySelector('[data-shorts-page-list]');
  const previousPage = root.querySelector('[data-shorts-page-prev]');
  const nextPage = root.querySelector('[data-shorts-page-next]');
  const pageStatus = root.querySelector('[data-shorts-page-status]');
  const pageSize = 12;
  const pageParam = 'shorts_page';
  const requestedPage = Number(new URL(window.location.href).searchParams.get(pageParam));
  let currentPage = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const modal = root.querySelector('[data-shorts-modal]');
  const dialog = root.querySelector('.ai-shorts-modal__dialog');
  const modalVisual = root.querySelector('.ai-shorts-modal__visual');
  const modalVideo = root.querySelector('[data-shorts-modal-video]');
  const modalControls = root.querySelector('[data-shorts-modal-controls]');
  const videoPlay = root.querySelector('[data-shorts-video-play]');
  const videoMute = root.querySelector('[data-shorts-video-mute]');
  const videoFullscreen = root.querySelector('[data-shorts-video-fullscreen]');
  const videoProgress = root.querySelector('[data-shorts-video-progress]');
  const videoTime = root.querySelector('[data-shorts-video-time]');
  const previewNode = root.querySelector('[data-shorts-preview-data]');
  const isLoggedIn = root.dataset.userLoggedIn === '1';
  const loginUrl = root.dataset.loginUrl || '';
  let previews = {};
  let trigger = null;
  let hls = null;
  let playbackRequest = null;
  let playbackSequence = 0;
  let hlsLoader = null;
  const formatVideoTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  };
  const syncVideoControls = () => {
    if (!modalVideo) return;
    const duration = Number.isFinite(modalVideo.duration) ? modalVideo.duration : 0;
    const currentTime = Number.isFinite(modalVideo.currentTime) ? modalVideo.currentTime : 0;
    if (videoPlay) {
      const paused = modalVideo.paused;
      videoPlay.firstElementChild.textContent = paused ? '▶' : 'Ⅱ';
      videoPlay.setAttribute('aria-label', paused ? 'Play episode' : 'Pause episode');
    }
    if (videoMute) {
      videoMute.firstElementChild.textContent = modalVideo.muted ? '🔇' : '🔊';
      videoMute.setAttribute('aria-label', modalVideo.muted ? 'Unmute episode' : 'Mute episode');
      videoMute.setAttribute('aria-pressed', modalVideo.muted ? 'true' : 'false');
    }
    if (videoProgress) {
      const progress = duration ? Math.round((currentTime / duration) * 1000) : 0;
      videoProgress.value = String(progress);
      videoProgress.parentElement.style.setProperty('--video-progress', `${progress / 10}%`);
    }
    if (videoTime) videoTime.textContent = `${formatVideoTime(currentTime)} / ${formatVideoTime(duration)}`;
  };
  if (modalVideo) {
    ['loadedmetadata', 'durationchange', 'timeupdate', 'play', 'pause', 'volumechange', 'ended'].forEach((eventName) => modalVideo.addEventListener(eventName, syncVideoControls));
  }
  if (videoPlay && modalVideo) videoPlay.addEventListener('click', () => modalVideo.paused ? modalVideo.play() : modalVideo.pause());
  if (videoMute && modalVideo) videoMute.addEventListener('click', () => { modalVideo.muted = !modalVideo.muted; });
  if (videoProgress && modalVideo) videoProgress.addEventListener('input', () => {
    const progress = Number(videoProgress.value);
    videoProgress.parentElement.style.setProperty('--video-progress', `${progress / 10}%`);
    if (Number.isFinite(modalVideo.duration)) modalVideo.currentTime = (progress / 1000) * modalVideo.duration;
  });
  if (videoFullscreen && modalVisual) videoFullscreen.addEventListener('click', async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (modalVisual.requestFullscreen) await modalVisual.requestFullscreen();
  });
  document.addEventListener('fullscreenchange', () => {
    if (!videoFullscreen) return;
    const fullscreen = document.fullscreenElement === modalVisual;
    videoFullscreen.setAttribute('aria-label', fullscreen ? 'Exit fullscreen' : 'Enter fullscreen');
    videoFullscreen.setAttribute('aria-pressed', fullscreen ? 'true' : 'false');
  });
  const loadHls = () => {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsLoader) return hlsLoader;
    hlsLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = root.dataset.hlsSrc;
      script.async = true;
      script.onload = () => window.Hls ? resolve(window.Hls) : reject(new Error('HLS player did not initialize'));
      script.onerror = () => reject(new Error('HLS player could not load'));
      document.head.append(script);
    });
    return hlsLoader;
  };
  try { previews = JSON.parse(previewNode ? previewNode.textContent : '{}'); } catch (error) { previews = {}; }
  const setUrlPage = () => {
    const url = new URL(window.location.href);
    if (currentPage > 1) url.searchParams.set(pageParam, currentPage);
    else url.searchParams.delete(pageParam);
    window.history.replaceState({}, '', url);
  };
  const updateCatalog = (resetPage = false, scrollToResults = false) => {
    if (resetPage) currentPage = 1;
    const category = filter ? filter.value : 'All';
    const matchingCards = cards.filter((card) => category === 'All'
      || category === 'New' && card.dataset.isNew === '1'
      || card.dataset.category === category);
    const pageCount = Math.ceil(matchingCards.length / pageSize);
    currentPage = Math.min(Math.max(currentPage, 1), Math.max(pageCount, 1));
    const pageStart = (currentPage - 1) * pageSize;
    const pageCards = new Set(matchingCards.slice(pageStart, pageStart + pageSize));
    cards.forEach((card) => { card.hidden = !pageCards.has(card); });
    if (count) count.textContent = matchingCards.length;
    if (empty) empty.hidden = matchingCards.length !== 0;
    if (pagination && pageList && previousPage && nextPage && pageStatus) {
      pagination.hidden = pageCount <= 1;
      previousPage.disabled = currentPage === 1;
      nextPage.disabled = currentPage >= pageCount;
      pageList.replaceChildren();
      const pageNumbers = pageCount <= 7
        ? Array.from({length: pageCount}, (_, index) => index + 1)
        : Array.from(new Set([1, currentPage - 1, currentPage, currentPage + 1, pageCount]
          .filter((number) => number >= 1 && number <= pageCount))).sort((a, b) => a - b);
      pageNumbers.forEach((number, index) => {
        if (index && number - pageNumbers[index - 1] > 1) {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'ai-shorts__pagination-ellipsis';
          ellipsis.textContent = '…';
          ellipsis.setAttribute('aria-hidden', 'true');
          pageList.append(ellipsis);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = number;
        button.setAttribute('aria-label', `AI Shorts page ${number}`);
        if (number === currentPage) button.setAttribute('aria-current', 'page');
        button.addEventListener('click', () => {
          currentPage = number;
          updateCatalog(false, true);
        });
        pageList.append(button);
      });
      const start = matchingCards.length ? pageStart + 1 : 0;
      const end = Math.min(pageStart + pageSize, matchingCards.length);
      pageStatus.textContent = matchingCards.length ? `Showing ${start}–${end} of ${matchingCards.length} series` : '';
    }
    setUrlPage();
    if (scrollToResults && grid) grid.scrollIntoView({behavior: 'smooth', block: 'start'});
  };
  if (filter) filter.addEventListener('change', () => updateCatalog(true));
  if (previousPage) previousPage.addEventListener('click', () => {
    if (currentPage > 1) {
      currentPage -= 1;
      updateCatalog(false, true);
    }
  });
  if (nextPage) nextPage.addEventListener('click', () => {
    currentPage += 1;
    updateCatalog(false, true);
  });
  updateCatalog();
  const hero = root.querySelector('[data-shorts-hero]');
  const heroData = root.querySelector('[data-shorts-hero-data]');
  const heroDots = [...root.querySelectorAll('[data-shorts-dot]')];
  let heroSlides = [];
  let heroTimer = null;
  try { heroSlides = JSON.parse(heroData ? heroData.textContent : '[]'); } catch (error) { heroSlides = []; }
  const activateHeroSlide = (dot) => {
    const slide = heroSlides[Number(dot.dataset.shortsDot)];
    if (!hero || !slide) return;
    heroDots.forEach((item) => item.setAttribute('aria-current', String(item === dot)));
    hero.style.setProperty('--shorts-art', `url("${String(slide.poster).replace(/["\\]/g, '\\$&')}")`);
    hero.querySelector('[data-shorts-hero-title]').textContent = slide.title;
    hero.querySelector('[data-shorts-hero-category]').textContent = slide.category;
    hero.querySelector('[data-shorts-hero-episodes]').textContent = slide.episodes;
    hero.querySelector('[data-shorts-hero-synopsis]').textContent = slide.synopsis;
    const action = hero.querySelector('[data-shorts-preview-trigger]');
    action.href = slide.destination;
    action.dataset.shortId = slide.slug;
  };
  const stopHeroAutoplay = () => {
    if (heroTimer) window.clearInterval(heroTimer);
    heroTimer = null;
  };
  const startHeroAutoplay = () => {
    stopHeroAutoplay();
    if (heroDots.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    heroTimer = window.setInterval(() => {
      if (document.hidden || modal && !modal.hidden) return;
      const current = heroDots.findIndex((dot) => dot.getAttribute('aria-current') === 'true');
      activateHeroSlide(heroDots[(current + 1) % heroDots.length]);
    }, 6000);
  };
  heroDots.forEach((dot) => dot.addEventListener('click', () => {
    activateHeroSlide(dot);
    startHeroAutoplay();
  }));
  if (hero) {
    hero.addEventListener('mouseenter', stopHeroAutoplay);
    hero.addEventListener('mouseleave', startHeroAutoplay);
    hero.addEventListener('focusin', stopHeroAutoplay);
    hero.addEventListener('focusout', (event) => { if (!hero.contains(event.relatedTarget)) startHeroAutoplay(); });
  }
  startHeroAutoplay();

  const closeModal = () => {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    const video = root.querySelector('[data-shorts-modal-video]');
    video.pause();
    video.removeAttribute('src');
    video.load();
    if (modalControls) modalControls.hidden = true;
    if (playbackRequest) { playbackRequest.abort(); playbackRequest = null; }
    if (hls) { hls.destroy(); hls = null; }
    document.body.classList.remove('has-shorts-modal');
    if (trigger) trigger.focus();
  };
  const isEpisodeLocked = (episode) => {
    const access = String(episode.access || '').toUpperCase();
    const restricted = access && !['FREE', 'PUBLIC', 'PREVIEW'].includes(access);
    return restricted || (!episode.id && !episode.video_url);
  };
  const playEpisode = async (episode, story, episodeKey = '') => {
    if (isEpisodeLocked(episode)) return;
    const sequence = ++playbackSequence;
    const video = root.querySelector('[data-shorts-modal-video]');
    const status = root.querySelector('[data-shorts-modal-status]');
    if (hls) { hls.destroy(); hls = null; }
    if (playbackRequest) playbackRequest.abort();
    playbackRequest = new AbortController();
    video.pause();
    if (modalControls) modalControls.hidden = true;
    video.autoplay = true;
    video.defaultMuted = false;
    video.muted = false;
    video.volume = 1;
    video.playsInline = true;
    root.querySelectorAll('.ai-shorts-modal__episode').forEach((item) => item.classList.toggle('is-active', item.dataset.episodeKey === episodeKey));
    root.querySelector('[data-shorts-modal-backdrop]').src = story.poster;
    video.poster = story.poster;
    video.removeAttribute('src');
    if (!episode.id && !episode.video_url) { status.textContent = 'Video preview is not available for this original concept.'; status.hidden = false; return; }
    status.textContent = 'Loading preview…';
    status.hidden = false;
    try {
      if (episode.video_url) {
        video.src = episode.video_url;
      } else {
        const response = await fetch(`${root.dataset.playbackBase}${encodeURIComponent(episode.id)}`, {signal: playbackRequest.signal});
        if (!response.ok) throw new Error('Playback request failed');
        const data = await response.json();
        if (sequence !== playbackSequence || modal.hidden) return;
        const Hls = await loadHls();
        if (sequence !== playbackSequence || modal.hidden) return;
        if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(data.playbackUrl);
          hls.attachMedia(video);
          await new Promise((resolve, reject) => {
            hls.once(Hls.Events.MANIFEST_PARSED, resolve);
            hls.once(Hls.Events.ERROR, (event, details) => { if (details.fatal) reject(new Error('HLS playback failed')); });
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) video.src = data.playbackUrl;
        else throw new Error('HLS playback is unsupported');
      }
      try {
        await video.play();
      } catch (playbackError) {
        if (playbackError.name !== 'NotAllowedError') throw playbackError;
        video.muted = true;
        await video.play();
        status.textContent = 'Playing muted by your browser. Use the volume control to enable audio.';
        status.hidden = false;
      }
      if (sequence !== playbackSequence || modal.hidden) return;
      if (modalControls) modalControls.hidden = false;
      if (!video.muted) status.hidden = true;
    } catch (error) {
      if (error.name === 'AbortError' || sequence !== playbackSequence) return;
      if (!video.paused) {
        if (modalControls) modalControls.hidden = false;
        status.hidden = true;
        return;
      }
      status.textContent = error.name === 'NotAllowedError' || /interrupted|save power/i.test(error.message)
        ? 'Tap play to start the preview.'
        : 'Preview could not load. Try again later.';
      if (modalControls) modalControls.hidden = error.name !== 'NotAllowedError';
      status.hidden = false;
    }
  };
  const openModal = (card) => {
    const story = previews[card.dataset.shortId];
    if (!modal || !dialog || !story) return;
    trigger = card;
    root.querySelector('[data-shorts-modal-title]').textContent = story.title;
    root.querySelector('[data-shorts-modal-category]').textContent = story.category;
    root.querySelector('[data-shorts-modal-synopsis]').textContent = story.synopsis;
    const list = root.querySelector('[data-shorts-modal-episodes]');
    list.replaceChildren();
    story.episodes.forEach((episode, index) => {
      const locked = isEpisodeLocked(episode);
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ai-shorts-modal__episode';
      item.dataset.episodeId = episode.id;
      item.dataset.episodeKey = String(index);
      const requiresLogin = locked && !isLoggedIn && loginUrl;
      item.disabled = locked && !requiresLogin;
      item.classList.toggle('is-locked', locked);
      item.classList.toggle('is-login-required', Boolean(requiresLogin));
      if (requiresLogin) item.title = `Log in to watch episode ${episode.number}`;
      else if (locked) item.title = `Episode ${episode.number} is locked`;
      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = `Episode ${episode.number}${episode.title ? ` · ${episode.title}` : ''}`;
      const meta = document.createElement('small');
      const minutes = episode.duration ? `${Math.floor(episode.duration / 60)}:${String(episode.duration % 60).padStart(2, '0')}` : '';
      meta.textContent = [episode.synopsis, minutes].filter(Boolean).join(' · ');
      copy.append(title, meta);
      const badge = document.createElement('b');
      if (requiresLogin) {
        badge.textContent = 'Log in to watch';
      } else if (locked) {
        const lockIcon = document.createElement('span');
        lockIcon.className = 'ai-shorts-modal__lock-icon';
        lockIcon.setAttribute('aria-hidden', 'true');
        lockIcon.textContent = '🔒';
        badge.append(lockIcon, document.createTextNode('Locked'));
      } else badge.textContent = index === 0 || episode.access === 'FREE' ? 'Preview' : 'Episode';
      item.append(copy, badge);
      if (requiresLogin) item.addEventListener('click', () => { window.location.href = loginUrl; });
      else if (!locked) item.addEventListener('click', () => playEpisode(episode, story, String(index)));
      list.append(item);
    });
    modal.hidden = false;
    if (modalControls) modalControls.hidden = true;
    document.body.classList.add('has-shorts-modal');
    dialog.focus();
    const playableIndex = story.episodes.findIndex((episode) => !isEpisodeLocked(episode));
    if (playableIndex >= 0) playEpisode(story.episodes[playableIndex], story, String(playableIndex));
    else {
      const video = root.querySelector('[data-shorts-modal-video]');
      const status = root.querySelector('[data-shorts-modal-status]');
      root.querySelector('[data-shorts-modal-backdrop]').src = story.poster;
      video.poster = story.poster;
      status.textContent = 'Episode previews are not available for this story.';
      status.hidden = false;
    }
  };
  previewTriggers.forEach((previewTrigger) => previewTrigger.addEventListener('click', (event) => {
    if (!previews[previewTrigger.dataset.shortId]) return;
    event.preventDefault();
    openModal(previewTrigger);
  }));
  root.querySelectorAll('[data-shorts-close]').forEach((button) => button.addEventListener('click', closeModal));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
    if (event.key === 'Tab' && modal && !modal.hidden) {
      const focusable = [...modal.querySelectorAll('button, a[href]')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
};
